import { useCallback } from 'react';
import { memoize, noop } from 'lodash';
import { UAParser } from 'ua-parser-js';
import { intl } from '@frameio/i18n';
import {
  useConsentOverride,
  useCookieConsentLastUpdate,
  useIsSegmentLoaded,
} from '@frameio/segment/contexts';
import { getOneTrustConsent } from '@frameio/segment/utils/getOneTrustConsent';
import { isServer } from '@frameio/utils';
import { getIdentifiedUserId } from '@frameio/utils/auth/shareAuth';
import { useActiveAccountId } from '../../contexts/ActiveEntitiesContext';
import { useClientNameContext } from '../../contexts/ClientNameContext';

const DEVICE_TYPE = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
};

type UserAgentData = {
  browserFamily: string;
  browserVersion: string;
  deviceManufacturer: string;
  deviceName: string;
  isMobile: boolean;
  isTablet: boolean;
  osName: string;
  osVersion: string;
  userAgent: string;
};

// Custom Partial type with null instead of undefined
type PartialWithNull<T> = {
  [P in keyof T]?: T[P] | null;
};

type UxpHostInfo = {
  hostVersion?: string | null;
  name?: string | null;
  version?: string | null;
  uiLocale?: string | null;
  pluginVersion?: string | null;
  schemaVersion?: string | null;
  uxpVersion?: string | null;
};

// Global storage for UXP host info
let globalUxpHostInfo: UxpHostInfo | null = null;

/**
 * Store UXP host info globally
 */
export const setUxpHostInfo = (hostInfo: UxpHostInfo) => {
  globalUxpHostInfo = hostInfo;
};

/**
 * Get UXP host info from global storage
 */
const getUxpHostInfo = (): UxpHostInfo | null => {
  return globalUxpHostInfo;
};
/**
 * Leverage `UAParser` to extract user agent specific details
 */
const getUserAgentData = isServer
  ? noop
  : memoize((): PartialWithNull<UserAgentData> => {
      const parser = new UAParser();
      const results = parser.getResult();

      const browserFamily = results?.browser?.name || null;
      const browserVersion = results?.browser?.version || null;
      const deviceManufacturer = results?.device?.vendor || null;
      const deviceName = results?.device?.model || null;
      const isMobile = results?.device?.type === DEVICE_TYPE.MOBILE;
      const isTablet = results?.device?.type === DEVICE_TYPE.TABLET;
      const osName = results?.os?.name || null;
      const osVersion = results?.os?.version || null;
      const userAgent = results?.ua || null;

      return {
        browserFamily,
        browserVersion,
        deviceManufacturer,
        deviceName,
        isMobile,
        isTablet,
        osName,
        osVersion,
        userAgent,
      };
    });

export type FrameIOContext = {
  account_id: string;
  browser: {
    family: string;
    version: string;
  };
  client: string;
  device: {
    manufacturer: string;
    name: string;
  };
  identified_user_id: string;
  language: string;
  is_mobile: boolean;
  is_tablet: boolean;
  os: {
    name: string;
    version: string;
  };
  version?: string;
};

export type ContextForAnalytics = FrameIOContext & Record<string, any>;

export type CoreOptionsForAnalytics = Record<string, any> & {
  context: ContextForAnalytics;
};

/**
 * Generic core options that are always needed in our identify calls
 *
 * https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=fiodata&title=Global+Properties
 */

export const useCoreOptionsForAnalytics = (isEdu?: boolean) => {
  const { isSegmentLoaded } = useIsSegmentLoaded();
  const { cookieConsentLastUpdate } = useCookieConsentLastUpdate();
  const consentOverride = useConsentOverride();
  const userAgentData = getUserAgentData() as unknown as UserAgentData;
  const accountId = useActiveAccountId();
  const client = useClientNameContext();
  const uxpHostInfo = getUxpHostInfo();
  const version = uxpHostInfo?.version;
  const { locale } = intl();

  /**
   * The core `options` field - a dictionary of options to use for every
   * segment event (populates `context` & integrations).
   *
   * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track
   */
  const coreOptions = useCallback((): CoreOptionsForAnalytics => {
    const identifiedUserId = getIdentifiedUserId();

    const consent = getOneTrustConsent(isEdu, consentOverride);

    return {
      context: {
        account_id: accountId,
        app: {
          version,
        },
        browser: {
          family: userAgentData?.browserFamily,
          version: userAgentData?.browserVersion,
        },
        client,
        device: {
          manufacturer: userAgentData?.deviceManufacturer,
          name: userAgentData?.deviceName,
        },
        identified_user_id: identifiedUserId ?? null,
        language: locale,
        is_mobile: userAgentData?.isMobile,
        is_tablet: userAgentData?.isTablet,
        os: {
          name: userAgentData?.osName,
          version: userAgentData?.osVersion,
        },
        screen: {
          height:
            typeof window !== 'undefined'
              ? window.screen.height?.toString()
              : undefined,
          width:
            typeof window !== 'undefined'
              ? window.screen.width?.toString()
              : undefined,
        },
        timezone: new Intl.DateTimeFormat('en-us').resolvedOptions().timeZone,
        ...consent,
      },
      integrations:
        typeof window !== 'undefined' &&
        isSegmentLoaded &&
        'integrations' in window
          ? (window.integrations as any)
          : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId,
    client,
    userAgentData,
    isSegmentLoaded,
    cookieConsentLastUpdate,
    consentOverride,
    isEdu,
    version,
    locale,
  ]);

  return {
    coreOptions,
  };
};
